import { getMessageHash } from '@iconik-io/shared-lib/src/utils/get_message_hash';
import { getPlainMessage } from '@iconik-io/shared-lib/src/utils/get_plain_message';

import type { AppDispatch, GetState } from '../../store/types';
import { hideMessage, showMessage } from './slice';
import type { Message } from './types';

const currentMessages = new Map<string, { timerId?: number } | null>();

export function clearMessage(messageHash: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const messageProps = currentMessages.get(messageHash);
    if (messageProps) {
      clearTimeout(messageProps.timerId);
    }

    currentMessages.delete(messageHash);
    const message = getState().messages[messageHash];

    if (!message) {
      return;
    }

    dispatch(hideMessage(messageHash));
  };
}

const autoClose =
  (message: Message) =>
  (dispatch: AppDispatch): number | undefined => {
    if (!message.autoclose) {
      return undefined;
    }
    const messageHash = getMessageHash(message);
    return window.setTimeout(() => {
      dispatch(clearMessage(messageHash));
    }, message.timeout);
  };

const prepareMessage =
  (messageArg: Message) =>
  (dispatch: AppDispatch): Message | null => {
    const message: Message = {
      ...messageArg,
      timeout: messageArg.timeout ?? 4000,
      autoclose: messageArg.autoclose ?? true,
    };

    const plainMessage = getPlainMessage(message.text);
    if (plainMessage.trim().length === 0) {
      return null;
    }

    const hash = getMessageHash(message);

    if (currentMessages.has(hash)) {
      // Re-run timer, so the message will just stay visible longer.
      const messageProps = currentMessages.get(hash);
      if (messageProps) {
        clearTimeout(messageProps.timerId);

        const timerId = dispatch(autoClose(message));
        currentMessages.set(hash, { ...messageProps, timerId });
      }
      return null;
    }

    const timerId = dispatch(autoClose(message));
    currentMessages.set(hash, { timerId });

    return messageArg;
  };

export type MessageParams = Omit<Message, 'variant'>;

export function successMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'success' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}

export function errorMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'danger' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}

export function warningMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'warning' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}

export function infoMessage(message: MessageParams) {
  return (dispatch: AppDispatch) => {
    const messagePayload = dispatch(
      prepareMessage({ ...message, variant: 'info' }),
    );
    if (!messagePayload) {
      return;
    }

    dispatch(showMessage(messagePayload));
  };
}
